<script setup>
import { WdsButtonGhost, WdsPillFloating, WdsPropertyCard } from '@wds/components';

const props = defineProps({
  properties: {
    type: Array,
    required: true,
  },
  title: {
    type: String,
    default: '',
  },
  viewMore: {
    type: Boolean,
    default: false,
  },
  viewMoreUrl: {
    type: String,
    default: '',
    required: false,
  },
  viewMoreText: {
    type: String,
    default: '',
    required: false,
  },
});

const emit = defineEmits(['propertyClick']);

const { useUrl } = useUrls();
const { useImage } = useImages();
const { useRating } = useRatings();
const { t } = useI18n();
const { useFormatter } = useFormatters();

// REF
const galleryList = ref(null);

// DATA
const activeGalleryItem = ref(0);
const galleryBoundaries = ref({
  left: true,
  right: false,
});

const getUrl = computed(() => {
  return props.viewMoreUrl;
});

// ### METHODS
function getGalleryItemFullWidth() {
  const galleryListRef = toValue(galleryList);
  if (galleryListRef) {
    const galleryFirstCard = galleryListRef.firstElementChild;
    return galleryFirstCard.offsetWidth;
  }

  return null;
}

function handleGalleryScroll(e) {
  const scrollEnd = e.currentTarget.scrollLeft;
  const itemWidth = getGalleryItemFullWidth();
  if (itemWidth) {
    activeGalleryItem.value = Math.floor(scrollEnd / itemWidth);
  }
}

function handleGalleryBoundaries(e) {
  const galleryListRef = toValue(galleryList);
  if (galleryListRef) {
    galleryBoundaries.value.left = e.currentTarget?.scrollLeft === 0;
    galleryBoundaries.value.right
      = e.currentTarget?.scrollLeft === galleryListRef?.scrollWidth - galleryListRef?.offsetWidth;
  }
}

function moveGalleryItem(direction) {
  const validDirection = [1, -1].includes(direction) ? direction : 1;
  const galleryListRef = toValue(galleryList);

  if (
    (validDirection < 0 && activeGalleryItem.value <= 0)
    || (validDirection > 0 && activeGalleryItem.value >= props.properties?.length - 1)
  ) {
    return;
  }

  if (galleryListRef) {
    activeGalleryItem.value += validDirection;
    moveGalleryToActive();
  }
}

function moveGalleryToActive() {
  const galleryListRef = toValue(galleryList);
  if (galleryListRef) {
    const galleryCards = galleryListRef.querySelectorAll('.filter-card');
    const nextCard = galleryCards?.[activeGalleryItem.value];
    if (nextCard) {
      nextCard?.scrollIntoView({
        behavior: 'smooth',
        inline: 'start',
        block: 'center',
      });
    }
  }
}

function getPriceFrom(property) {
  const privateRoomsPrice = property.privateMinPrice?.value || 0;
  const dormRoomsPrice = property.sharedMinPrice?.value || 0;
  const pricesWithoutZeros = [privateRoomsPrice, dormRoomsPrice].filter((n) => Number(n) > 0).map(Number);
  const propertyMinPrice = pricesWithoutZeros.length ? Math.min.apply(0, pricesWithoutZeros) : null;
  property.minPriceString = propertyMinPrice
    ? useFormatter.formatCurrency(propertyMinPrice, property.sharedMinPrice.currency)
    : '';
  return property.minPriceString || null;
}

function getPropertyCardLink(property) {
  const propertyTypeInfo = findPropertyTypeByKey(property.type);
  return useUrl.getPropertyPageUrl({
    propertyTypeSlug: t(propertyTypeInfo.slug),
    propertyId: property.id,
    urlFriendlyProperty: property.urlFriendlyName,
  });
}

function getPropertyDetailsForCard(property) {
  const propertyImage = useImage.getImage(property?.image?.small) || null;
  const propSlides = propertyImage ? [useImage.getImage(propertyImage)] : [];
  const propertyMinPrice = getPriceFrom(property);
  const propertyDistance
    = Number.isFinite(property?.cityCenterDistance)
      ? t('t_FROMCITYCENTER', { DISTANCE: property?.cityCenterDistance })
      : (property?.cityCenterDistance || null);
  return {
    name: property?.name,
    href: getPropertyCardLink(property),
    distance: propertyDistance,
    gallery: {
      slides: propSlides,
      alt: property?.name,
      title: property?.name,
    },
    rating: {
      score: property?.avgRating,
      totalReviews: property?.numberReviews,
      translations: toValue(useRating.getRatingScoreKeywordTranslations),
    },
    fromPrice: propertyMinPrice ? { curPrice: propertyMinPrice } : null,
    translations: {
      tPrivatesFrom: t('t_PRIVATESFROM'),
      tDormsFrom: t('t_DORMSFROM'),
      tNoPrivatesAvailable: t('t_NOPRIVATESAVAILABLE'),
      tNoDormsAvailable: t('t_NODORMSAVAILABLE'),
      tNoAvailability: t('t_NOAVAILABILITY'),
      tPricesFrom: t('t_PRICESFROM'),
    },
  };
}

function onPropertyCardClick(property) {
  emit('propertyClick', property);
}

onMounted(() => {
  const galleryListRef = toValue(galleryList);
  if (galleryListRef) {
    galleryListRef?.addEventListener('scrollend', handleGalleryScroll, false);
    galleryListRef?.addEventListener('scroll', handleGalleryBoundaries, false);
  }
});

onUnmounted(() => {
  const galleryListRef = toValue(galleryList);
  if (galleryListRef) {
    galleryListRef?.removeEventListener('scrollend', handleGalleryScroll, false);
    galleryListRef?.removeEventListener('scroll', handleGalleryBoundaries, false);
  }
});
</script>

<template>
  <div
    v-if="properties?.length"
    class="cards-container"
  >
    <div class="nav">
      <div class="filter-card-navigation">
        <h2
          class="title"
          v-html="title"
        />
        <div class="align-right">
          <WdsPillFloating
            icon-start="arrow-left"
            aria-label="left arrow"
            :disabled="galleryBoundaries.left"
            @click="moveGalleryItem(-1)"
          />
          <WdsPillFloating
            icon-start="arrow-right"
            aria-label="right arrow"
            :disabled="galleryBoundaries.right"
            @click="moveGalleryItem(1)"
          />
        </div>
      </div>
    </div>

    <div class="top-filters-wrapper">
      <div class="card-gradient" />
      <div
        ref="galleryList"
        class="filter-card-list"
      >
        <WdsPropertyCard
          v-for="(property, index) in properties"
          :key="property.id"
          :show-pricing="true"
          v-bind="getPropertyDetailsForCard(property)"
          class="filter-card"
          @click="onPropertyCardClick(property, index)"
        />
      </div>
    </div>
    <div
      v-if="props.viewMore && props.viewMoreUrl"
      class="top-view-more"
    >
      <WdsButtonGhost
        :href="getUrl"
        :text="viewMoreText || $t('t_VIEWMORE')"
        icon-end="chevron-right"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.cards-container {
  .nav {
    display: grid;
    grid-template-columns: 1fr;

    .filter-card-navigation {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: $wds-spacing-m;

      .align-left {
        flex: 1;
        text-align: left;
      }

      .align-right {
        display: none;
      }

      @include desktop {
        .align-right {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: $wds-spacing-m;
        }
      }

      h2.title {
        @include title-3-bld;

        text-align: left;
        color: $wds-color-ink-darker;

        :deep(span) {
          color: $wds-color-purple;
        }

        @include tablet-large {
          @include title-1-bld;
        }
      }
    }

    @include desktop {
      .filter-card-navigation {
        display: flex;
      }
    }
  }

  .top-filters-wrapper {
    overflow: hidden;
    width: 100%;
    position: relative;

    .filter-card-list {
      display: inline-flex;
      gap: $wds-spacing-m;
      overflow-x: auto;
      scroll-behavior: smooth;
      scroll-snap-type: x mandatory;
      padding: $wds-spacing-m 0;
      width: 100%;

      @include no-scrollbar;

      .filter-card {
        min-width: wds-rem(260px);
        scroll-snap-align: start;
      }

      @include tablet {
        gap: $wds-spacing-xl;
        padding: $wds-spacing-xl 0;

        .filter-card {
          min-width: wds-rem(310px);
        }
      }

      @include desktop {
        gap: $wds-spacing-l;
        padding: $wds-spacing-l 0;
      }

      > * {
        flex: 0 0 wds-rem(260px);
        height: auto;
      }
    }

    .card-gradient {
      position: absolute;
      top: $wds-spacing-l;
      bottom: $wds-spacing-l;
      right: 0;
      width: 80px;
      z-index: 200;
      background: linear-gradient(270deg, #fff 0%, rgb(255 255 255 / 0%) 100%);
    }
  }

  .top-view-more {
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }
}
</style>
